/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { checkInApi } from '../../../hooks/useCheckInDesk';
import CertificateRender from '../../athlohive/CertificateRender';
import { exportCertificate } from '../../athlohive/certificateExport';
import { loadCertificateMap, useCertificateMap, withCertificateMap } from '../../athlohive/certificateMap';
import { certificateValues } from '../../athlohive/certificateFiller';
import { utcToWallClock } from '../../../utils/zonedDateTime';
import { infoOf } from './checkInRules';
import {
  CHECKED_IN, CERTIFICATE_TABLE, EMAIL_MAX, MOBILE_DIGITS, NAME_MAX, POSITION_DIGITS,
  buildGenerateBatch, buildRegenerateBatch, countryCodeInput, detailsForm, fetchPassNow, finishTimeInput, onlyDigits,
  saveBatch, validateDetails,
} from './certificatesTab';
import { fetchRows } from './checkInApi';

const shell = () => document.querySelector('.org-shell') || document.body;
const PREVIEW_WIDTH = 420;

function Field({ label, required, error, hint, children, wide }) {
  return (
    <label className={`ocrt-field${wide ? ' is-wide' : ''}`}>
      <span>{label}{required && <em> *</em>}</span>
      {children}
      {error ? <small className="ocrt-err">{error}</small> : hint && <small className="ocrt-hint">{hint}</small>}
    </label>
  );
}

function Guard({ icon, tone, title, children }) {
  return (
    <div className={`ocrt-guard is-${tone}`} role="alert">
      <i className={`bi ${icon}`} aria-hidden="true" />
      <div><b>{title}</b>{children}</div>
    </div>
  );
}

/**
 * Generate one participant's certificate. Not checked in, or no template for
 * their activity → it says so and stops. Otherwise: confirm or correct the
 * name, email and number on the pass, add an optional finish time and
 * position, watch the certificate update, then Save & generate — one batch
 * that saves the pass and creates the certificate. An issued certificate is
 * shown read-only; Regenerate makes it editable again and saves the pass's
 * details and that certificate row, keeping its number, and the participant
 * is emailed again (the record.updated hook).
 */
const regenerateForm = (row) => ({
  ...detailsForm(row.info),
  finishTime: String(row.certificate?.finish_time || ''),
  position: row.certificate?.position === null || row.certificate?.position === undefined ? '' : String(row.certificate.position),
});

export default function GenerateCertificateDialog({ row, event, course, mode = 'generate', onClose, onGenerated }) {
  const [regenerating, setRegenerating] = useState(mode === 'regenerate' && Boolean(row.certificate));
  const [form, setForm] = useState(() => (row.certificate ? regenerateForm(row) : detailsForm(row.info)));
  const [showErrors, setShowErrors] = useState(false);
  const [touched, setTouched] = useState({});
  const [phase, setPhase] = useState({ step: 'edit', message: '' });
  const [issued, setIssued] = useState(row.certificate);
  const [download, setDownload] = useState({ busy: '', error: '' }); // busy: '' | 'pdf' | 'png'

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && phase.step !== 'saving') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, phase.step]);

  const errors = validateDetails(form);
  // A field's error shows once it has been left, or for every field after a save attempt.
  const visible = showErrors ? errors : Object.fromEntries(Object.entries(errors).filter(([key]) => touched[key]));
  const set = (key, filter = (v) => v) => (e) => {
    const value = filter(e.target.value);
    setForm((current) => ({ ...current, [key]: value }));
  };
  const leave = (key) => () => setTouched((current) => ({ ...current, [key]: true }));
  const eventDate = utcToWallClock(event?.start_datetime, event?.timezone) || '';

  // Read-only: an issued certificate that is not being regenerated.
  const locked = Boolean(issued) && !regenerating;
  const values = useMemo(() => certificateValues({
    firstName: locked ? row.info.first_name : form.firstName,
    lastName: locked ? row.info.last_name : form.lastName,
    eventTitle: event?.title || '',
    activityName: row.activityName,
    city: event?.location || '',
    organizationName: row.choice?.organization_name || '',
    logoUrl: row.choice?.organization_logo_url || '',
    certificateNo: issued ? issued.certificate_no : 'Given on generate',
    finishTime: locked ? issued.finish_time : form.finishTime,
    position: locked ? issued.position : form.position,
    eventDate,
    routeLine: course?.routeLine || null,
    venueName: course?.venueName || '',
  }), [form, issued, locked, row, event, course, eventDate]);
  // The real map (OpenStreetMap tiles with the course or venue) once it is built.
  const mapImage = useCertificateMap(course);
  const shown = useMemo(() => withCertificateMap(values, mapImage), [values, mapImage]);

  const generate = async () => {
    if (Object.keys(errors).length) { setShowErrors(true); return; }
    setPhase({ step: 'saving', message: '' });
    try {
      // The list may be minutes old: check the pass as it is now.
      const pass = await fetchPassNow(checkInApi, row.passId);
      if (!pass || !CHECKED_IN.includes(pass.status)) {
        setPhase({ step: 'not_checked_in', message: '' });
        return;
      }
      await saveBatch(checkInApi, buildGenerateBatch({ ...row, info: infoOf(pass.user_info) || row.info }, form, event.id));
      const [created] = await fetchRows(checkInApi, CERTIFICATE_TABLE, {
        filters: [{ column_key: 'pass_id', filter_type: 'equals', column_value: row.passId }], pageSize: 1,
      });
      setIssued(created || { certificate_no: '' });
      setPhase({ step: 'done', message: form.email.trim() });
      onGenerated();
    } catch (error) {
      const duplicate = /unique|already exists|duplicate/i.test(JSON.stringify(error?.body || error?.message || ''));
      setPhase({
        step: 'edit',
        message: duplicate
          ? 'This participant already has a certificate. Refresh the list to see it.'
          : (error?.message || 'The certificate was not generated. Nothing was saved.'),
      });
    }
  };

  const regenerate = async () => {
    if (Object.keys(errors).length) { setShowErrors(true); return; }
    setPhase({ step: 'saving', message: '' });
    try {
      const pass = await fetchPassNow(checkInApi, row.passId);
      await saveBatch(checkInApi, buildRegenerateBatch({ ...row, info: infoOf(pass?.user_info) || row.info }, issued, form));
      const [updated] = await fetchRows(checkInApi, CERTIFICATE_TABLE, {
        filters: [{ column_key: 'pass_id', filter_type: 'equals', column_value: row.passId }], pageSize: 1,
      });
      setIssued(updated || issued);
      setRegenerating(false);
      setPhase({ step: 'regenerated', message: form.email.trim() });
      onGenerated();
    } catch (error) {
      setPhase({ step: 'edit', message: error?.message || 'The certificate was not updated. Nothing was saved.' });
    }
  };

  // Built from the template and this certificate's own data, every time:
  // the same page as the preview, captured and saved as a PDF or PNG.
  const downloadAs = async (format) => {
    setDownload({ busy: format, error: '' });
    try {
      // Wait for the map so the file always has it (falls back to the styled one if it cannot be built).
      const map = await loadCertificateMap(course || {}).catch(() => '');
      await exportCertificate({ templateUrl: row.template?.url, values: withCertificateMap(values, map), format, certificateNo: issued?.certificate_no });
      setDownload({ busy: '', error: '' });
    } catch (error) {
      setDownload({ busy: '', error: error?.message || 'The certificate could not be prepared. Please try again.' });
    }
  };

  const notCheckedIn = phase.step === 'not_checked_in' || (!issued && !CHECKED_IN.includes(row.status));
  const noTemplate = !issued && !notCheckedIn && (!row.choice || !row.template);
  const canEdit = (!issued && !notCheckedIn && !noTemplate) || (Boolean(issued) && regenerating);
  const saving = phase.step === 'saving';

  return createPortal(
    <div className="ochd-scrim" role="presentation" onClick={() => !saving && onClose()}>
      <div className="ochd ocrt-dialog" role="dialog" aria-modal="true" aria-label="Generate certificate" onClick={(e) => e.stopPropagation()}>
        <header className="ochd-head">
          <div className="ocrt-who">
            <span className="ocrt-avatar" aria-hidden="true">{(row.name || row.passcode || '?').slice(0, 1).toUpperCase()}</span>
            <div>
              <h2>{regenerating ? 'Regenerate certificate' : (issued ? 'Certificate' : 'Generate certificate')}</h2>
              <p className="ocrt-sub">
                {row.name || 'Unnamed participant'}
                {row.passcode && <> · <span className="ochk-code">{row.passcode}</span></>}
                {row.activityName && <> · {row.activityName}</>}
              </p>
            </div>
          </div>
          <button type="button" className="ochk-iconbtn" onClick={onClose} aria-label="Close" disabled={saving}>
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>

        {notCheckedIn && (
          <Guard icon="bi-person-x" tone="warn" title="This pass is not checked in.">
            <p>
              A certificate can only be generated for a participant who has checked in. Please ask the participant
              to check in using their pass, or check them in yourself by scanning their pass on the Scanner. Once
              they are checked in, return here to generate the certificate.
            </p>
            <Link className="org-btn ghost ocrt-small" to="/organizer/check-in">
              <i className="bi bi-qr-code-scan" aria-hidden="true" /> Go to Scanner
            </Link>
          </Guard>
        )}

        {noTemplate && (
          <Guard icon="bi-award" tone="warn" title={`No certificate template is set for ${row.activityName || 'this activity'}.`}>
            <p>Choose one in the event&rsquo;s step 4, “Certificate templates”.</p>
            <Link className="org-btn ghost ocrt-small" to={`/organizer/events/${event.id}/edit`}>
              <i className="bi bi-pencil-square" aria-hidden="true" /> Edit event
            </Link>
          </Guard>
        )}

        {phase.step === 'regenerated' && (
          <div className="ocrt-success" role="status">
            <i className="bi bi-arrow-repeat" aria-hidden="true" />
            <div>
              <b>Certificate updated · {issued?.certificate_no}</b>
              <p>Same certificate number. The updated download link is being emailed to <b>{phase.message}</b>.</p>
            </div>
          </div>
        )}

        {regenerating && phase.step !== 'regenerated' && (
          <p className="ocrt-note ocrt-regen-note">
            <i className="bi bi-info-circle" aria-hidden="true" /> Change the details below and save. The certificate keeps its number <b className="ochk-code">{issued?.certificate_no}</b>, and the participant is emailed the updated certificate.
          </p>
        )}

        {phase.step === 'done' && (
          <div className="ocrt-success" role="status">
            <i className="bi bi-patch-check-fill" aria-hidden="true" />
            <div>
              <b>Certificate generated{issued?.certificate_no ? ` · ${issued.certificate_no}` : ''}</b>
              <p>The download link is being emailed to <b>{phase.message}</b>.</p>
            </div>
          </div>
        )}

        {(canEdit || issued) && (
          <div className="ocrt-body">
            <div className="ocrt-form">
              {issued && !regenerating && phase.step !== 'done' && phase.step !== 'regenerated' && (
                <div className="ocrt-issued">
                  <span className="ocrt-chip is-issued"><i className="bi bi-patch-check-fill" aria-hidden="true" /> Issued</span>
                  <span className="ochk-code">{issued.certificate_no}</span>
                </div>
              )}
              <fieldset disabled={!canEdit || saving} className="ocrt-fieldset">
                <legend>Participant details <small>saved on the pass</small></legend>
                <div className="ocrt-grid">
                  <Field label="First name" required error={visible.firstName}>
                    <input className="orgf-input" value={locked ? row.info.first_name || '' : form.firstName} maxLength={NAME_MAX} onChange={set('firstName')} onBlur={leave('firstName')} autoComplete="off" />
                  </Field>
                  <Field label="Last name" required error={visible.lastName}>
                    <input className="orgf-input" value={locked ? row.info.last_name || '' : form.lastName} maxLength={NAME_MAX} onChange={set('lastName')} onBlur={leave('lastName')} autoComplete="off" />
                  </Field>
                  <Field label="Email" required wide error={visible.email} hint={<><i className="bi bi-envelope-exclamation" aria-hidden="true" /> Please enter the correct email address. The certificate link will be sent to this email.</>}>
                    <input className="orgf-input" type="email" value={locked ? row.info.email || '' : form.email} maxLength={EMAIL_MAX} onChange={set('email', (v) => v.replace(/\s/g, ''))} onBlur={leave('email')} autoComplete="off" />
                  </Field>
                  <Field label="Mobile number" wide error={visible.countryCode || visible.mobileNumber} hint={`Optional · ${MOBILE_DIGITS} digits`}>
                    <div className="ocrt-phone">
                      <input className="orgf-input ocrt-cc" value={locked ? row.info.country_code || '' : form.countryCode} maxLength={4} onChange={set('countryCode', countryCodeInput)} onBlur={leave('countryCode')} aria-label="Country code" />
                      <input className="orgf-input" inputMode="numeric" value={locked ? row.info.mobile_number || '' : form.mobileNumber} maxLength={MOBILE_DIGITS} placeholder={`${MOBILE_DIGITS} digits`} onChange={set('mobileNumber', (v) => onlyDigits(v, MOBILE_DIGITS))} onBlur={leave('mobileNumber')} aria-label="Mobile number" />
                    </div>
                  </Field>
                </div>
              </fieldset>
              <fieldset disabled={!canEdit || saving} className="ocrt-fieldset">
                <legend>Result <small>optional · printed only where the template shows it</small></legend>
                <div className="ocrt-grid">
                  <Field label="Finish time" error={visible.finishTime} hint="Type the digits, e.g. 014530 → 01:45:30">
                    <input className="orgf-input" inputMode="numeric" placeholder="HH:MM:SS" maxLength={8} value={locked ? issued.finish_time || '' : form.finishTime} onChange={set('finishTime', finishTimeInput)} onBlur={leave('finishTime')} />
                  </Field>
                  <Field label="Position" error={visible.position} hint="Overall place, like 12">
                    <input className="orgf-input" inputMode="numeric" placeholder="—" maxLength={POSITION_DIGITS} value={locked ? issued.position ?? '' : form.position} onChange={set('position', (v) => onlyDigits(v, POSITION_DIGITS))} onBlur={leave('position')} />
                  </Field>
                </div>
              </fieldset>
              {locked && phase.step !== 'done' && phase.step !== 'regenerated' && (
                <p className="ocrt-note"><i className="bi bi-lock" aria-hidden="true" /> To correct anything, use <b>Regenerate</b>. The certificate keeps its number.</p>
              )}
            </div>

            <aside className="ocrt-preview" aria-label="Certificate preview">
              <p className="org-lab">Live preview · {row.template?.name || 'Template'}</p>
              {row.template && <CertificateRender template={row.template} width={PREVIEW_WIDTH} values={shown} />}
              {issued && !regenerating && row.template && (
                <div className="ocrt-downloads">
                  <button type="button" className="org-btn ghost" onClick={() => downloadAs('pdf')} disabled={Boolean(download.busy)}>
                    <i className={`bi ${download.busy === 'pdf' ? 'bi-arrow-repeat orgc-spin' : 'bi-file-earmark-pdf'}`} aria-hidden="true" />
                    {download.busy === 'pdf' ? 'Preparing PDF…' : 'Download PDF'}
                  </button>
                  <button type="button" className="org-btn ghost" onClick={() => downloadAs('png')} disabled={Boolean(download.busy)}>
                    <i className={`bi ${download.busy === 'png' ? 'bi-arrow-repeat orgc-spin' : 'bi-file-earmark-image'}`} aria-hidden="true" />
                    {download.busy === 'png' ? 'Preparing PNG…' : 'Download PNG'}
                  </button>
                </div>
              )}
              {download.error && <p className="ocrt-err" role="alert">{download.error}</p>}
              <p className="ocrt-hint">
                Organization: <b>{row.choice?.organization_name || '—'}</b>
                {!issued && ' · the certificate number is given on generate'}
              </p>
            </aside>
          </div>
        )}

        {phase.message && phase.step === 'edit' && <p className="ocrt-err ocrt-failure" role="alert">{phase.message}</p>}

        <footer className="ocrt-actions">
          {regenerating ? (
            <>
              <button type="button" className="org-btn ghost" disabled={saving}
                onClick={() => { setRegenerating(false); setForm(regenerateForm(row)); setShowErrors(false); setPhase({ step: 'edit', message: '' }); }}>
                Cancel
              </button>
              <button type="button" className="org-btn" onClick={regenerate} disabled={saving}>
                <i className={`bi ${saving ? 'bi-arrow-repeat orgc-spin' : 'bi-arrow-repeat'}`} aria-hidden="true" />
                {saving ? 'Saving…' : 'Save & regenerate'}
              </button>
            </>
          ) : canEdit && phase.step !== 'done' ? (
            <>
              <button type="button" className="org-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className="org-btn" onClick={generate} disabled={saving}>
                <i className={`bi ${saving ? 'bi-arrow-repeat orgc-spin' : 'bi-award'}`} aria-hidden="true" />
                {saving ? 'Generating…' : 'Save & generate certificate'}
              </button>
            </>
          ) : (
            <>
              {issued && (
                <button type="button" className="org-btn ghost" disabled={Boolean(download.busy)}
                  onClick={() => { setForm(regenerateForm({ ...row, certificate: issued })); setTouched({}); setShowErrors(false); setPhase({ step: 'edit', message: '' }); setRegenerating(true); }}>
                  <i className="bi bi-arrow-repeat" aria-hidden="true" /> Regenerate
                </button>
              )}
              <button type="button" className="org-btn" onClick={onClose}>{phase.step === 'done' || phase.step === 'regenerated' ? 'Done' : 'Close'}</button>
            </>
          )}
        </footer>
      </div>
    </div>,
    shell(),
  );
}

